import FilterCardDisclosureController from "../lib/filter_card_disclosure"

// Shared Date card for HAML surfaces (agent dashboard, Email Services
// analytics / bounces) — the server-rendered twin of the React
// DateRangeDropdown (#13753): presets, From/To and the optional "Use Created
// Date" box only edit a DRAFT; Done submits the form once; Cancel / X /
// Escape / outside click put the committed state back.
//
// The form carries a hidden `date_preset` field (target presetField): a
// preset pill writes its key, a manual date edit writes "custom", a cleared
// or partial range writes "" so the server falls back to the surface's own
// default. Every pill carries its resolved range (data-from / data-to,
// computed server-side in the user's zone) so the inputs echo the preset
// exactly as the server will resolve it.
export default class extends FilterCardDisclosureController {
  static targets = ["wrapper", "button", "panel", "presetField", "preset", "from", "to", "dateType"]

  readCommitted() {
    return {
      preset: this.hasPresetFieldTarget ? this.presetFieldTarget.value : "",
      from: this.hasFromTarget ? this.fromTarget.value : "",
      to: this.hasToTarget ? this.toTarget.value : "",
      dateType: this.hasDateTypeTarget ? this.dateTypeTarget.checked : false
    }
  }

  restoreDraft() {
    const c = this.committed
    if (!c) return
    if (this.hasPresetFieldTarget) this.presetFieldTarget.value = c.preset
    if (this.hasFromTarget) this.fromTarget.value = c.from
    if (this.hasToTarget) this.toTarget.value = c.to
    if (this.hasDateTypeTarget) this.dateTypeTarget.checked = c.dateType
    this.paintPreset(c.preset)
  }

  draft() {
    return {
      preset: this.hasPresetFieldTarget ? this.presetFieldTarget.value : "",
      from: this.hasFromTarget ? this.fromTarget.value : "",
      to: this.hasToTarget ? this.toTarget.value : "",
      dateType: this.hasDateTypeTarget ? this.dateTypeTarget.checked : false
    }
  }

  // Preset pill: draft only — echo its range into From/To and light the pill.
  pickPreset(event) {
    event.preventDefault()
    const pill = event.currentTarget
    if (this.hasFromTarget) this.fromTarget.value = pill.dataset.from || ""
    if (this.hasToTarget) this.toTarget.value = pill.dataset.to || ""
    if (this.hasPresetFieldTarget) this.presetFieldTarget.value = pill.dataset.key || ""
    this.paintPreset(pill.dataset.key)
  }

  // A manual date edit deselects the pill. To never falls before From:
  // editing From drags To up with it; editing To is floored at From.
  fromChanged() {
    const from = this.fromTarget.value
    if (this.hasToTarget && from && this.toTarget.value && this.toTarget.value < from) this.toTarget.value = from
    this.markCustom()
  }

  toChanged() {
    const from = this.hasFromTarget ? this.fromTarget.value : ""
    if (from && this.toTarget.value && this.toTarget.value < from) this.toTarget.value = from
    this.markCustom()
  }

  markCustom() {
    if (this.hasPresetFieldTarget) this.presetFieldTarget.value = "custom"
    this.paintPreset(null)
  }

  // Clear empties the inputs and deselects the pill but does NOT close or
  // submit — the draft is reset so the user can re-pick (React parity);
  // focus returns to From for immediate entry.
  clear(event) {
    event?.preventDefault()
    if (this.hasFromTarget) this.fromTarget.value = ""
    if (this.hasToTarget) this.toTarget.value = ""
    if (this.hasPresetFieldTarget) this.presetFieldTarget.value = ""
    this.paintPreset(null)
    if (this.hasFromTarget) this.fromTarget.focus({ preventScroll: true })
  }

  // Done → one form submit. A partial range (only From or only To) commits as
  // CLEARED, exactly like the React card: no preset, no dates, and the
  // created-date box reset, so the server applies its default window.
  // Returns true when a submit was dispatched (the page is going away).
  commit() {
    const draft = this.draft()
    const complete = Boolean(draft.from && draft.to)
    if (!complete) {
      if (this.hasFromTarget) this.fromTarget.value = ""
      if (this.hasToTarget) this.toTarget.value = ""
      if (this.hasPresetFieldTarget) this.presetFieldTarget.value = ""
      if (this.hasDateTypeTarget) this.dateTypeTarget.checked = false
    }
    if (this.unchanged(this.draft())) return false
    const form = this.form()
    if (!form) return false
    form.requestSubmit()
    return true
  }

  unchanged(draft) {
    const c = this.committed
    return Boolean(c) && draft.preset === c.preset && draft.from === c.from &&
      draft.to === c.to && draft.dateType === c.dateType
  }

  // `null` lights nothing (custom / cleared). An EMPTY key is a real pill on a
  // surface whose default is all time (data-key="" — #14040), so it is matched
  // like any other key rather than read as "no preset".
  paintPreset(key) {
    if (!this.hasPresetTarget) return
    this.presetTargets.forEach((pill) => {
      pill.classList.toggle("active", key != null && pill.dataset.key === key)
    })
  }
}
